/**
 * Users Section
 * 
 * Full user management interface for the admin area.
 * Migrated from AdminPanel drawer tab with full CRUD operations.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Clear as ClearIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { userApi, type User, type CreateUserRequest } from '../../services/api';
import { UserDialog } from '../../components/UserDialog';
import { useSudo } from '../../hooks/useSudo';

export function UsersSection() {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'admins' | 'viewers'>('viewers');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingOperation, setPendingOperation] = useState<'creating' | 'saving' | 'mfa-reset' | 'deleting' | null>(null);

  const navigate = useNavigate();
  const { startSudo } = useSudo();
  const queryClient = useQueryClient();

  // Users query
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: userApi.getUsers,
  });

  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => userApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
      setUserDialogOpen(false);
      setEditingUser(null);
      setPendingOperation(null);
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message || 'Failed to create user', severity: 'error' });
      setPendingOperation(null);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateUserRequest> & { newPassword?: string; resetMfa?: boolean } }) =>
      userApi.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const message = variables.data.resetMfa
        ? 'User updated and MFA reset. Share the new password with the user.'
        : 'User updated successfully';
      setSnackbar({ open: true, message, severity: 'success' });
      setUserDialogOpen(false);
      setEditingUser(null);
      setPendingOperation(null);
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message || 'Failed to update user', severity: 'error' });
      setPendingOperation(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      setUserDialogOpen(false);
      setEditingUser(null);
      setPendingOperation(null);
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message || 'Failed to delete user', severity: 'error' });
      setPendingOperation(null);
    },
  });

  /**
   * Get user's display email (without embed- prefix for viewers)
   */
  const getDisplayEmail = (user: User) => {
    if (user.userType === 'admin') {
      return user.email || '';
    }
    return user.externalUserId?.replace(/^embed-/, '') || '';
  };

  const getInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    const fallback = getDisplayEmail(user);
    return fallback?.[0]?.toUpperCase() || '?';
  };

  /**
   * Get partner attribute from user attributes
   */
  const getPartner = (user: User) => {
    return user.userAttributes?.partner as string || '-';
  };

  /**
   * Filter users by type
   */
  const adminUsers = useMemo(() => users.filter(u => u.userType === 'admin'), [users]);
  const viewerUsers = useMemo(() => users.filter(u => u.userType === 'viewer'), [users]);

  /**
   * Filter users based on search query (name, email, partner)
   */
  const filteredViewerUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return viewerUsers;
    }

    const query = searchQuery.toLowerCase();
    return viewerUsers.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = getDisplayEmail(user).toLowerCase();
      const partner = getPartner(user).toLowerCase();

      return fullName.includes(query) || email.includes(query) || partner.includes(query);
    });
  }, [viewerUsers, searchQuery]);

  const handleOpenAddUser = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setEditingUser(null);
    setUserDialogOpen(true);
  };

  const handleOpenEditUser = (user: User) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setEditingUser(user);
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
  };

  const handleUserSubmit = (data: CreateUserRequest & { newPassword?: string; resetMfa?: boolean }) => {
    if (editingUser) {
      setPendingOperation(data.resetMfa ? 'mfa-reset' : 'saving');
      updateUserMutation.mutate({
        id: editingUser.id,
        data: {
          userType: data.userType,
          firstName: data.firstName,
          lastName: data.lastName,
          permissions: data.permissions,
          models: data.models,
          groupIds: data.groupIds,
          externalGroupId: data.externalGroupId,
          userAttributes: data.userAttributes,
          folderIds: data.folderIds,
          themeName: data.themeName,
          newPassword: data.newPassword,
          resetMfa: data.resetMfa,
        },
      });
    } else {
      setPendingOperation('creating');
      createUserMutation.mutate(data);
    }
  };

  const handleDeleteUser = (id: string) => {
    setPendingOperation('deleting');
    deleteUserMutation.mutate(id);
  };

  const handleViewAs = (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    startSudo(user);
    navigate('/');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const currentUsers = activeTab === 'admins' ? adminUsers : filteredViewerUsers;

  const loadingMessage =
    pendingOperation === 'deleting'  ? 'Deleting user...' :
    pendingOperation === 'mfa-reset' ? 'Resetting MFA — this may take a moment...' :
    pendingOperation === 'saving'    ? 'Saving changes...' :
    pendingOperation === 'creating'  ? 'Creating user...' :
    undefined;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create and manage viewer accounts for embedded dashboard access
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleOpenAddUser}
          sx={{
            background: 'linear-gradient(135deg, #18181b 0%, #2d2d44 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2d2d44 0%, #3d3d5c 100%)',
            },
          }}
        >
          Add User
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
          }}
        >
          <Tab
            label={
              searchQuery
                ? `Viewers (${filteredViewerUsers.length} of ${viewerUsers.length})`
                : `Viewers (${viewerUsers.length})`
            }
            value="viewers"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
          <Tab
            label={`Admins (${adminUsers.length})`}
            value="admins"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          />
        </Tabs>

        {activeTab === 'viewers' && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, or partner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : currentUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
            <Typography color="text.secondary">
              {activeTab === 'viewers' && searchQuery
                ? `No viewers found matching "${searchQuery}"`
                : `No ${activeTab === 'admins' ? 'admin' : 'viewer'} users found.`}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  {activeTab === 'viewers' && <TableCell>Partner</TableCell>}
                  <TableCell>Type</TableCell>
                  <TableCell>Models / Access</TableCell>
                  <TableCell>Created</TableCell>
                  {activeTab === 'viewers' && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => user.userType === 'viewer' && handleOpenEditUser(user)}
                    sx={{
                      cursor: user.userType === 'viewer' ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: user.userType === 'viewer' ? 'action.hover' : 'inherit',
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: user.userType === 'admin' ? '#6366f1' : '#3d3d5c',
                            fontSize: '0.75rem',
                          }}
                        >
                          {getInitials(user)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getDisplayEmail(user)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {activeTab === 'viewers' && (
                      <TableCell>
                        <Typography variant="body2">
                          {getPartner(user)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={user.userType === 'admin' ? 'Admin' : 'Viewer'}
                        size="small"
                        color={user.userType === 'admin' ? 'primary' : 'default'}
                        variant={user.userType === 'admin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {user.userType === 'admin' ? (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Full access (Okta/Cognito)
                        </Typography>
                      ) : user.models && user.models.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {user.models.slice(0, 2).map((model) => (
                            <Chip key={model} label={model} size="small" color="secondary" />
                          ))}
                          {user.models.length > 2 && (
                            <Chip label={`+${user.models.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No models
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    {activeTab === 'viewers' && (
                      <TableCell align="center">
                        {user.userType === 'viewer' && (
                          <Tooltip title="View as this user">
                            <IconButton
                              size="small"
                              onClick={(e) => handleViewAs(user, e)}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* User Dialog */}
      <UserDialog
        open={userDialogOpen}
        onClose={handleCloseUserDialog}
        user={editingUser}
        onSubmit={handleUserSubmit}
        onDelete={handleDeleteUser}
        isLoading={createUserMutation.isPending || updateUserMutation.isPending || deleteUserMutation.isPending}
        loadingMessage={loadingMessage}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default UsersSection;
